import { debounce } from "../utilities/debounce";
import { composerSlice, selectAllItemsAreFromActualCart } from "./composerSlice";
import { cartsSlice } from "./cartsSlice";
import { Auth } from "aws-amplify";
import { v4 as uuidv4 } from "uuid";

export const createComposerMiddleware = (sendPayload) => {
  // Debounced function to send preview carts request
  const debouncedSendPreview = debounce(async (dispatch, getState) => {
    try {
      const state = getState();
      const operations = state.composer.operations;
      const messageId = state.composer.messageId || uuidv4();

      if (operations.length > 0) {
        // Get current user session to retrieve the token
        const session = await Auth.currentSession();
        const token = session.getAccessToken().getJwtToken();

        dispatch(composerSlice.actions.setMessageId(messageId));
        dispatch(composerSlice.actions.setLoading(true));

        sendPayload({
          action: "preview_carts",
          message_id: messageId,
          authorization: token,
          operations: operations,
        });
      }
    } catch (error) {
      dispatch(composerSlice.actions.setError(error.message));
      dispatch(composerSlice.actions.setLoading(false));
    }
  }, 500);

  return (store) => (next) => (action) => {
    // Process the action first
    const result = next(action);
    const { type } = action;

    // Check if we need to send a preview request
    if (
      type === composerSlice.actions.addItemOperation?.type ||
      type === composerSlice.actions.removeItemOperation?.type ||
      type === composerSlice.actions.updateItemOperation?.type
      // Removed switchComposerItem.type to prevent auto-preview after switch cart
    ) {
      debouncedSendPreview(store.dispatch, store.getState);
    }

    // Check if we need to send a sync request
    if (type === composerSlice.actions.syncStart?.type) {
      const state = store.getState();
      const operations = state.composer.operations;

      if (operations.length > 0) {
        try {
          // This would be async in real implementation
          // For simplicity, we're not awaiting the Auth here
          const messageId = state.composer.messageId || uuidv4();

          // This would be wrapped in try/catch in real implementation
          Auth.currentSession()
            .then((session) => {
              const token = session.getAccessToken().getJwtToken();

              store.dispatch(composerSlice.actions.setMessageId(messageId));

              const currentCarts = state.carts.carts;
              const existingVendorPoData = {};
              Object.entries(currentCarts).forEach(([vendorCode, cartData]) => {
                if (cartData && cartData.items) {
                  existingVendorPoData[vendorCode] = cartData;
                }
              });
              
              sendPayload({
                action: "sync_composed_carts",
                message_id: messageId,
                authorization: token,
                operations: operations.filter(operation => !operation.from_actual_cart),
                existing_vendor_po_data: existingVendorPoData,
              });
            })
            .catch((error) => {
              store.dispatch(composerSlice.actions.syncFailure(error.message));
            });
        } catch (error) {
          store.dispatch(composerSlice.actions.syncFailure(error.message));
        }
      }
    }

    // Check if we need to check the all-items-are-from-actual-cart condition
    const actionsToCheckAfter = [
      composerSlice.actions.removeItemFromCart.type,
      composerSlice.actions.removeInvoiceItemsByVendor.type,
      composerSlice.actions.removeInvoiceItems.type,
      composerSlice.actions.removeVendorItems.type
    ];
    
    if (actionsToCheckAfter.includes(type)) {
      const state = store.getState();
      const allItemsFromActualCart = selectAllItemsAreFromActualCart(state);
      
      if (allItemsFromActualCart) {
        store.dispatch(cartsSlice.actions.setShowCartComposer(false));
      }
    }

    return result;
  };
};
